import warningIcon from "$assets/icons/warning-icon.svg?url";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { SelectField, TextField } from "$components/rhf-fields";
import {
  applyExchangedToDue,
  buildCurrencyExchange,
  invoiceToExchangedValue,
  type SelectedCurrencyExchange,
} from "$routes/_lib/billing-v2/currency-exchange";
import {
  useConvertFromPenceFactory,
  useConvertFromPoundsFactory,
} from "$routes/_lib/use-convert-currency";
import { useCurrencyExchangeFormat } from "$routes/_lib/use-currency-exchange-format";
import { useLatestExchangeRate } from "$routes/patient/[:patientId]/_lib/invoices-v2/exchange-rate-provider";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Grid,
  InputAdornment,
  makeStyles,
  Typography,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import { type BillingCurrency, TransactionType } from "elephant-web/gql";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { number, object, string } from "yup";
import useCurrencyFormatter from "../../hooks/useCurrencyFormatter";
import { ExchangeRateNote } from "../ExchangeRateNote";
import useRefundInvoice from "./hooks/useRefundInvoice";

const useStyles = makeStyles((theme) => ({
  modal: {
    borderRadius: theme.spacing(3),
  },
  totalAmount: {
    marginTop: "4px",
    fontSize: "24px",
    fontWeight: 700,
  },
  warningMessage: {
    color: theme.palette.grey[600],
    fontWeight: 400,
    marginLeft: theme.spacing(0.5),
  },
  warningIcon: {
    marginRight: theme.spacing(2),
  },
}));

export const REASON = "reason";
export const AMOUNT_TO_REFUND = "amountToRefund";
const CURRENCY = "currency";

const useRefundSchema = ({ totalRefundableInPence }) => {
  const penceToPounds = useConvertFromPenceFactory();

  return useMemo(
    () =>
      object().shape({
        [REASON]: string().required(),
        [AMOUNT_TO_REFUND]: number()
          .required()
          .positive()
          .max(penceToPounds(totalRefundableInPence)),
      }),
    [totalRefundableInPence, penceToPounds],
  );
};

type FormState = {
  reason: string;
  amountToRefund: number;
  currency: string;
};

export const RefundInvoiceModal: FC<{
  invoiceId: string;
  open: boolean;
  onClose: () => void;
  currency: string;
  totalRefundableInPence: number;
}> = ({ invoiceId, open, onClose, currency, totalRefundableInPence }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { refundInvoice } = useRefundInvoice(invoiceId);
  const { currencyFormatter } = useCurrencyFormatter();
  const { formatMoney, currencySymbol } = useCurrencyExchangeFormat();
  const latestRate = useLatestExchangeRate(currency as BillingCurrency);

  const penceToPounds = useConvertFromPenceFactory();
  const poundsToPence = useConvertFromPoundsFactory();
  const [busy, setBusy] = useState(false);
  const [exchange, setExchange] = useState<SelectedCurrencyExchange>();
  const refundableInSelectedCurrency = exchange
    ? invoiceToExchangedValue(
        totalRefundableInPence,
        exchange.currency,
        exchange.rate,
      )
    : totalRefundableInPence;

  const handleErrorMessage = (value) =>
    value?.id ? formatMessage(value) : value;

  const handleRefund = async ({
    reason,
    amountToRefund: entered,
  }: FormState) => {
    setBusy(true);
    try {
      const enteredInMinorUnits = poundsToPence(entered);
      if (!exchange) {
        await refundInvoice({ reason, amountToRefund: enteredInMinorUnits });
        return;
      }
      const amountToRefund = applyExchangedToDue({
        exchangedValue: enteredInMinorUnits,
        exchangedCurrency: exchange.currency,
        rate: exchange.rate,
        due: totalRefundableInPence,
      });
      if (amountToRefund === null) {
        throw new Error("Refund exceeds the refundable amount");
      }
      await refundInvoice({
        reason,
        amountToRefund,
        currencyExchange: buildCurrencyExchange({
          type: TransactionType.Refund,
          invoiceSide: {
            currency: currency as BillingCurrency,
            value: amountToRefund,
          },
          exchanged: {
            currency: exchange.currency,
            value: enteredInMinorUnits,
          },
          exchangeRate: exchange.rate,
        }),
      });
    } finally {
      onClose();
    }
  };

  const amountLabel = formatMessage({
    id: "invoicing.refund.amount.placeholder",
    defaultMessage: "Amount to Refund",
  });

  const reasonLabel = formatMessage({
    id: "invoicing.refund.reason.label",
    defaultMessage: "Reason for refunding the invoice",
  });

  const reasonPlaceholder = formatMessage({
    id: "invoicing.refund.reason.placeholder",
    defaultMessage: "Reason for refunding the invoice",
  });

  const initialValues = useMemo(
    () => ({
      [REASON]: undefined,
      [AMOUNT_TO_REFUND]: penceToPounds(totalRefundableInPence),
      [CURRENCY]: currency,
    }),
    [totalRefundableInPence, penceToPounds, currency],
  );

  const formMethods = useForm<FormState>({
    defaultValues: initialValues,
    resolver: yupResolver(
      useRefundSchema({
        totalRefundableInPence: refundableInSelectedCurrency,
      }),
    ),
    mode: "onTouched",
  });

  const watchedCurrency = useWatch({
    control: formMethods.control,
    name: CURRENCY,
  });
  const currencyRef = useRef(watchedCurrency);
  useEffect(() => {
    if (currencyRef.current === watchedCurrency) return;
    currencyRef.current = watchedCurrency;
    const switchedExchange =
      latestRate && watchedCurrency === latestRate.otherCurrency
        ? { currency: latestRate.otherCurrency, rate: latestRate.exchangeRate }
        : undefined;
    setExchange(switchedExchange);
    formMethods.setValue(
      AMOUNT_TO_REFUND,
      penceToPounds(
        switchedExchange
          ? invoiceToExchangedValue(
              totalRefundableInPence,
              switchedExchange.currency,
              switchedExchange.rate,
            )
          : totalRefundableInPence,
      ),
    );
  }, [
    watchedCurrency,
    latestRate,
    totalRefundableInPence,
    penceToPounds,
    formMethods,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-validate once the switched currency's schema is in place
  useEffect(() => {
    formMethods.trigger(AMOUNT_TO_REFUND);
  }, [exchange, formMethods]);

  return (
    <DialogContainer open={open} className={classes.modal}>
      <FormProvider {...formMethods}>
        <form
          name="refund-invoice-form"
          onSubmit={formMethods.handleSubmit(handleRefund)}
        >
          <DialogHeader title="">
            <Box display="flex" alignItems="center">
              <img
                src={warningIcon}
                alt="warning"
                className={classes.warningIcon}
              />
              <Typography gutterBottom variant="h5">
                <FormattedMessage
                  id="invoicing.refund.confirmation.title"
                  defaultMessage="You are about to refund this invoice"
                />
              </Typography>
            </Box>
          </DialogHeader>
          <DialogBody>
            <Grid container direction="row" justifyContent="space-between">
              <Grid item xs={4}>
                <FormattedMessage
                  id="invoicing.refund.totalAmountLabel"
                  defaultMessage="Amount Paid ({currency})"
                  values={{
                    currency,
                  }}
                />
                <div className={classes.totalAmount}>
                  {currencyFormatter(totalRefundableInPence)}
                </div>
                {exchange && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    data-testid="invoicing.refund.totalInOtherCurrency"
                  >
                    <FormattedMessage
                      id="invoicing.refund.totalInOtherCurrency"
                      defaultMessage="{amount} in {currency}"
                      values={{
                        amount: formatMoney(
                          exchange.currency,
                          refundableInSelectedCurrency,
                        ),
                        currency: exchange.currency,
                      }}
                    />
                  </Typography>
                )}
              </Grid>

              {latestRate && (
                <Grid item xs={4}>
                  <SelectField
                    name={CURRENCY}
                    label={formatMessage({
                      id: "invoicing.refund.currency.label",
                      defaultMessage: "Currency",
                    })}
                    options={[currency, latestRate.otherCurrency].map(
                      (code) => ({
                        value: code,
                        label: `${currencySymbol(code)} (${code})`,
                      }),
                    )}
                    disabled={busy}
                    fullWidth
                  />
                </Grid>
              )}
              <Grid item xs={4}>
                <TextField
                  inputProps={{
                    min: "1",
                    step: ".01",
                  }}
                  type="number"
                  name={AMOUNT_TO_REFUND}
                  label={amountLabel}
                  InputProps={
                    exchange && {
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          data-testid="invoicing.refund.exchangedCurrency"
                        >
                          {currencySymbol(exchange.currency)}
                        </InputAdornment>
                      ),
                    }
                  }
                  disabled={busy}
                  helperText={
                    formMethods.formState.touchedFields[AMOUNT_TO_REFUND] &&
                    handleErrorMessage(
                      formMethods.formState.errors[AMOUNT_TO_REFUND],
                    )
                  }
                  data-testid={"invoicing.refund.amountToRefund"}
                  required
                />
              </Grid>
              {exchange && (
                <Grid item xs={12}>
                  <ExchangeRateNote rate={exchange.rate} />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  name={REASON}
                  label={reasonLabel}
                  placeholder={reasonPlaceholder}
                  required
                  multiline
                  minRows={5}
                  fullWidth
                  data-testid={"invoicing.refund.reason"}
                  disabled={busy}
                  helperText={
                    formMethods.formState.touchedFields[REASON] &&
                    handleErrorMessage(formMethods.formState.errors[REASON])
                  }
                />
              </Grid>
            </Grid>
          </DialogBody>
          <DialogFooter>
            <Button color="secondary" onClick={() => onClose()} disabled={busy}>
              <FormattedMessage
                id="invoicing.refund.confirmation.cancel"
                defaultMessage="Cancel"
              />
            </Button>
            <Button
              color="primary"
              data-testid="invoicing.refund.confirmation.accept"
              startIcon={<CheckIcon />}
              loading={busy}
              disabled={
                formMethods.formState.isSubmitting ||
                !formMethods.formState.isValid
              }
              type="submit"
            >
              <FormattedMessage
                id="invoicing.refund.confirmation.accept"
                defaultMessage="Refund"
              />
            </Button>
          </DialogFooter>
        </form>
      </FormProvider>
    </DialogContainer>
  );
};

export default RefundInvoiceModal;
